import { addListener, createListenerMiddleware } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/store/store'
import { ApiError } from '@/util/errors/api-error'
import { getEditingLeaseError } from '@/editing-leases/schemas'
import { isHeldByAnotherUser } from '@/editing-leases/utils/is-held-by-another-user'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { UppyFileUploadActions } from '@/uppy-file-upload/store/slice'
import { kMedChronAssetPollIntervalsMs } from '../constants'
import { getMedChronAsset } from '../fetch/get-med-chron-asset'
import { getMedChronAssetFiles } from '../fetch/get-med-chron-asset-files'
import { getMedChronAssetUpdates } from '../fetch/get-med-chron-asset-updates'
import { getUpdateStatus } from '../fetch/get-update-status'
import { postInitializeChronology } from '../fetch/post-initialize-chronology'
import { postApplyUpdate } from '../fetch/post-apply-update'
import { postRetryFailed } from '../fetch/post-retry-failed'
import { postCompleteExcludingFailed } from '../fetch/post-complete-excluding-failed'
import { refreshMatterAfterWriteAccessError } from '../utils/refresh-matter-after-write-access-error'
import { kActiveMedChronUpdateStatuses, MedChronUpdateStatus } from './schemas'
import { MedChronAssetActions, type MedChronSubmissionError } from './slice'
import { loadMedChronAssetData, loadMedChronWorkspace } from './load-workspace'

export const medChronAssetListenerMiddleware = createListenerMiddleware()
export const addMedChronAssetListener = addListener.withTypes<RootState, AppDispatch>()
const startListener = medChronAssetListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()

const kConflictMessage = 'The chronology is being updated. Try again when the current update finishes.'
const kGenericMutationMessage = 'Something went wrong. Please try again.'

function showSubmissionErrorToast(error: MedChronSubmissionError, dispatch: AppDispatch): void {
  const message =
    typeof error === 'string'
      ? error
      : isHeldByAnotherUser(error.lease)
      ? `${error.lease.holder_display_name || 'Another editor'} is editing. You cannot make changes until they are done.`
      : 'Something went wrong. Try refreshing the page.'
  dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message, durationMs: kToastDurationMediumMs }))
}

function matterIdForAsset(state: RootState, assetId: string): string | undefined {
  return Object.values(state.medChronAssetState.assetByMatterId).find((asset) => asset?.id === assetId)?.matter_id
}

// Give up polling after this many consecutive status-fetch failures (transient errors retry).
const kMedChronAssetMaxPollFailures = 5

// Assets with an in-flight poll loop. De-duping per asset (instead of a global
// listener unsubscribe) lets different matters/assets poll concurrently — a global
// take-leading guard dropped a second matter's poll when you switched to it and never
// resumed it. Exported so tests can reset it between cases (module-level state).
export const pollingAssetIds = new Set<string>()

/**
 * Listener: Fetch Asset For Matter
 * Loads the matter's living chronology overview (or its confirmed absence), then resumes
 * polling of any in-flight update run and refreshes events/files when stale.
 */
startListener({
  actionCreator: MedChronAssetActions.fetchAssetForMatter,
  effect: async (action, listenerApi) => {
    try {
      await loadMedChronWorkspace(action.payload, listenerApi)
    } catch {
      // The loader records overview/data failures in their respective UI state.
    }
  },
})

/**
 * Listener: Refresh Asset Data
 * Fetches the ordered live events + source-file roster. Events/references go into the
 * shared medical-chronology slices keyed by assetId (the table/popovers read by store key).
 */
startListener({
  actionCreator: MedChronAssetActions.refreshAssetData,
  effect: async (action, listenerApi) => {
    try {
      await loadMedChronAssetData(action.payload.assetId, listenerApi.dispatch)
    } catch {
      // The loader records the failure; action-driven refreshes have no awaiting caller.
    }
  },
})

/**
 * Listener: Fetch Update History
 * Loads the asset's full update history (newest-first) into the store for the version panel.
 */
startListener({
  actionCreator: MedChronAssetActions.fetchUpdateHistory,
  effect: async (action, listenerApi) => {
    const { assetId } = action.payload

    try {
      const response = await getMedChronAssetUpdates(assetId)
      listenerApi.dispatch(MedChronAssetActions.setUpdateHistory({ assetId, updates: response.updates }))
    } catch (e) {
      console.error('Error fetching medical chronology update history:', e)
    }
  },
})

/**
 * Listener: Poll Active Update
 * Polls one update run with 5s -> 10s -> 15s backoff until it leaves the active statuses.
 * De-dupes per asset so re-dispatching for an asset already polling is a no-op, while
 * different assets still poll concurrently (so switching matters doesn't drop a poll).
 * Exits: DONE -> refresh overview + data; FAILED_FILES -> keep status + refresh roster;
 * ERROR -> keep status (update.id doubles as the user-facing error ID).
 */
startListener({
  actionCreator: MedChronAssetActions.pollActiveUpdate,
  effect: async (action, listenerApi) => {
    const { assetId, updateId } = action.payload

    if (pollingAssetIds.has(assetId)) return
    pollingAssetIds.add(assetId)
    try {
      let pollCount = 0
      let consecutiveErrors = 0

      for (;;) {
        // A transient status-fetch error must not kill the loop; swallow it and retry on
        // the next backoff tick so the banner keeps polling instead of spinning forever.
        let status
        try {
          status = await getUpdateStatus(assetId, updateId)
          consecutiveErrors = 0
        } catch (e) {
          consecutiveErrors++
          console.error(`Error polling medical chronology update (failure ${consecutiveErrors}/${kMedChronAssetMaxPollFailures}):`, e)
          if (consecutiveErrors >= kMedChronAssetMaxPollFailures) return
          const delay = kMedChronAssetPollIntervalsMs[Math.min(Math.floor(pollCount / 3), kMedChronAssetPollIntervalsMs.length - 1)]
          pollCount++
          await listenerApi.delay(delay)
          continue
        }

        listenerApi.dispatch(MedChronAssetActions.setUpdateStatus({ assetId, status }))

        if (kActiveMedChronUpdateStatuses.includes(status.update.status)) {
          const delay = kMedChronAssetPollIntervalsMs[Math.min(Math.floor(pollCount / 3), kMedChronAssetPollIntervalsMs.length - 1)]
          pollCount++
          await listenerApi.delay(delay)
          continue
        }

        if (status.update.status === MedChronUpdateStatus.DONE) {
          const overview = await getMedChronAsset(assetId)
          listenerApi.dispatch(MedChronAssetActions.setAssetForMatter({ matterId: overview.matter_id, asset: overview }))
          listenerApi.dispatch(MedChronAssetActions.refreshAssetData({ assetId }))
          listenerApi.dispatch(MedChronAssetActions.fetchUpdateHistory({ assetId }))
        } else if (status.update.status === MedChronUpdateStatus.FAILED_FILES) {
          const files = await getMedChronAssetFiles(assetId)
          listenerApi.dispatch(MedChronAssetActions.setFilesForAsset({ assetId, files: files.files }))
        }
        return
      }
    } catch (e) {
      console.error('Error polling medical chronology update:', e)
    } finally {
      // Release the per-asset guard so a later run on this asset (or coming back to the matter)
      // can start a fresh poll loop. Without this the asset stays "polling" forever and re-dispatches no-op.
      pollingAssetIds.delete(assetId)
    }
  },
})

/**
 * Listener: Initialize Chronology
 * Creates the living chronology from the staged file selection and starts polling its
 * INITIALIZE run. Handle typed lease denials first; other 409s require an overview
 * refresh for the matter's existing chronology.
 */
startListener({
  actionCreator: MedChronAssetActions.initializeChronology,
  effect: async (action, listenerApi) => {
    const { matterId, userFileIds } = action.payload

    try {
      listenerApi.dispatch(MedChronAssetActions.setInitializePending({ matterId, isPending: true }))
      listenerApi.dispatch(MedChronAssetActions.setInitializeError({ matterId, error: null }))

      const response = await postInitializeChronology({ matter_id: matterId, user_file_ids: userFileIds })
      listenerApi.dispatch(MedChronAssetActions.setAssetForMatter({ matterId, asset: response.asset }))
      listenerApi.dispatch(MedChronAssetActions.pollActiveUpdate({ assetId: response.asset.id, updateId: response.update.id }))
      // The staged batch is now submitted; clear it so re-entering Add Files starts clean.
      listenerApi.dispatch(MedChronAssetActions.resetAddFilesStaging({ matterId }))
    } catch (e) {
      const leaseError = getEditingLeaseError(e)
      if (leaseError) {
        listenerApi.dispatch(MedChronAssetActions.setInitializeError({ matterId, error: leaseError }))
        listenerApi.dispatch(MedChronAssetActions.editingLeaseRejected(leaseError))
        showSubmissionErrorToast(leaseError, listenerApi.dispatch)
        return
      }
      refreshMatterAfterWriteAccessError(e, matterId, listenerApi.dispatch)
      const message = e instanceof ApiError && e.status === 409 ? 'This matter already has a chronology.' : kGenericMutationMessage
      listenerApi.dispatch(MedChronAssetActions.setInitializeError({ matterId, error: message }))
      if (e instanceof ApiError && e.status === 409) {
        listenerApi.dispatch(MedChronAssetActions.fetchAssetForMatter({ matterId }))
      }
    } finally {
      listenerApi.dispatch(MedChronAssetActions.setInitializePending({ matterId, isPending: false }))
    }
  },
})

/**
 * Listener: Apply Update (Add Files)
 * Enqueues an ADD_FILES run and starts polling it. Typed lease denials update workspace
 * availability; failures show an error toast, and other 409s refetch the active run's overview.
 */
startListener({
  actionCreator: MedChronAssetActions.applyUpdate,
  effect: async (action, listenerApi) => {
    const { assetId, addUserFileIds } = action.payload

    try {
      listenerApi.dispatch(MedChronAssetActions.setMutationPending({ assetId, isPending: true }))
      listenerApi.dispatch(MedChronAssetActions.setMutationError({ assetId, error: null }))

      const response = await postApplyUpdate(assetId, { add_user_file_ids: addUserFileIds })

      const overview = await getMedChronAsset(assetId)
      listenerApi.dispatch(MedChronAssetActions.setAssetForMatter({ matterId: overview.matter_id, asset: overview }))
      // The roster rows for the added files are created synchronously when the run is enqueued, so
      // refresh the Source Files roster now — they show as Pending/Processing instead of only after DONE.
      const files = await getMedChronAssetFiles(assetId)
      listenerApi.dispatch(MedChronAssetActions.setFilesForAsset({ assetId, files: files.files }))
      listenerApi.dispatch(MedChronAssetActions.pollActiveUpdate({ assetId, updateId: response.update.id }))
      // The staged batch is now submitted; clear it so re-entering Add Files starts clean.
      listenerApi.dispatch(MedChronAssetActions.resetAddFilesStaging({ matterId: overview.matter_id }))
    } catch (e) {
      const leaseError = getEditingLeaseError(e)
      if (leaseError) {
        listenerApi.dispatch(MedChronAssetActions.setMutationError({ assetId, error: leaseError }))
        listenerApi.dispatch(MedChronAssetActions.editingLeaseRejected(leaseError))
        showSubmissionErrorToast(leaseError, listenerApi.dispatch)
        return
      }
      refreshMatterAfterWriteAccessError(e, matterIdForAsset(listenerApi.getState(), assetId), listenerApi.dispatch)
      const message = e instanceof ApiError && e.status === 409 ? kConflictMessage : kGenericMutationMessage
      listenerApi.dispatch(MedChronAssetActions.setMutationError({ assetId, error: message }))
      showSubmissionErrorToast(message, listenerApi.dispatch)
      if (e instanceof ApiError && e.status === 409) {
        try {
          const overview = await getMedChronAsset(assetId)
          listenerApi.dispatch(MedChronAssetActions.setAssetForMatter({ matterId: overview.matter_id, asset: overview }))
          if (overview.active_update) {
            listenerApi.dispatch(MedChronAssetActions.pollActiveUpdate({ assetId, updateId: overview.active_update.id }))
          }
        } catch (refetchError) {
          console.error('Error refetching medical chronology after conflict:', refetchError)
        }
      }
    } finally {
      listenerApi.dispatch(MedChronAssetActions.setMutationPending({ assetId, isPending: false }))
    }
  },
})

/**
 * Listener: Retry Failed Files
 * Re-enqueues a FAILED_FILES run for its failed files only, then resumes polling.
 */
startListener({
  actionCreator: MedChronAssetActions.retryFailedFiles,
  effect: async (action, listenerApi) => {
    const { assetId, updateId } = action.payload

    try {
      listenerApi.dispatch(MedChronAssetActions.setMutationPending({ assetId, isPending: true }))
      listenerApi.dispatch(MedChronAssetActions.setMutationError({ assetId, error: null }))

      const response = await postRetryFailed(assetId, updateId)
      // Refresh the overview so active_update points to the retry run. Otherwise the stale
      // FAILED_FILES active_update keeps selectFailedFilesUpdate returning the old decision, so the
      // "Retry / Continue without failed files" banner stays up while the retry is already running.
      const overview = await getMedChronAsset(assetId)
      listenerApi.dispatch(MedChronAssetActions.setAssetForMatter({ matterId: overview.matter_id, asset: overview }))
      listenerApi.dispatch(MedChronAssetActions.pollActiveUpdate({ assetId, updateId: response.update.id }))
    } catch (e) {
      const leaseError = getEditingLeaseError(e)
      if (leaseError) {
        listenerApi.dispatch(MedChronAssetActions.setMutationError({ assetId, error: leaseError }))
        listenerApi.dispatch(MedChronAssetActions.editingLeaseRejected(leaseError))
        return
      }
      refreshMatterAfterWriteAccessError(e, matterIdForAsset(listenerApi.getState(), assetId), listenerApi.dispatch)
      listenerApi.dispatch(MedChronAssetActions.setMutationError({ assetId, error: kGenericMutationMessage }))
    } finally {
      listenerApi.dispatch(MedChronAssetActions.setMutationPending({ assetId, isPending: false }))
    }
  },
})

/**
 * Listener: Complete Excluding Failed
 * Publishes the staged successes of a FAILED_FILES run and excludes its failures,
 * then refreshes the overview and chronology data.
 */
startListener({
  actionCreator: MedChronAssetActions.completeExcludingFailed,
  effect: async (action, listenerApi) => {
    const { assetId, updateId } = action.payload

    try {
      listenerApi.dispatch(MedChronAssetActions.setMutationPending({ assetId, isPending: true }))
      listenerApi.dispatch(MedChronAssetActions.setMutationError({ assetId, error: null }))

      const status = await postCompleteExcludingFailed(assetId, updateId)
      listenerApi.dispatch(MedChronAssetActions.setUpdateStatus({ assetId, status }))

      const overview = await getMedChronAsset(assetId)
      listenerApi.dispatch(MedChronAssetActions.setAssetForMatter({ matterId: overview.matter_id, asset: overview }))
      listenerApi.dispatch(MedChronAssetActions.refreshAssetData({ assetId }))
    } catch (e) {
      const leaseError = getEditingLeaseError(e)
      if (leaseError) {
        listenerApi.dispatch(MedChronAssetActions.setMutationError({ assetId, error: leaseError }))
        listenerApi.dispatch(MedChronAssetActions.editingLeaseRejected(leaseError))
        return
      }
      refreshMatterAfterWriteAccessError(e, matterIdForAsset(listenerApi.getState(), assetId), listenerApi.dispatch)
      listenerApi.dispatch(MedChronAssetActions.setMutationError({ assetId, error: kGenericMutationMessage }))
    } finally {
      listenerApi.dispatch(MedChronAssetActions.setMutationPending({ assetId, isPending: false }))
    }
  },
})

/**
 * Listener: Stage Initialized Upload
 * Converts a pending Add Files upload into a staged file the moment its user_file id exists.
 * Keyed on the shared uppy action because BOTH upload drivers dispatch it — the Add Files
 * page pipeline and the MatterLayout fallback that adopts uploads stranded when the page
 * unmounts mid-batch. Living here (not in the page hook) is what guarantees a fallback-driven
 * upload still lands in the staged selection instead of deadlocking the CTA as a forever-pending
 * card. Uploads not registered by the med-chron Add Files page are never in the attached list.
 */
startListener({
  actionCreator: UppyFileUploadActions.fileInitialized,
  effect: (action, listenerApi) => {
    const { clientTmpId, serverFileId } = action.payload
    const attachedPendingIds = listenerApi.getState().medChronAssetState.addFilesStaging.attachedPendingUploadClientIds
    if (!attachedPendingIds.includes(clientTmpId)) return

    listenerApi.dispatch(MedChronAssetActions.stageUserFileIds({ fileIds: [serverFileId] }))
    listenerApi.dispatch(MedChronAssetActions.removePendingUserFileUpload(clientTmpId))
  },
})
